/**
 * Interruptores para lo que el diseño ya tiene y el backend todavía no.
 *
 * Todo arranca apagado. Se prende en build con `VITE_FLAG_<NOMBRE>=true`
 * (por ejemplo `VITE_FLAG_NOTIFICATIONS=true`). No hay flags remotos a
 * propósito: son para no mostrar una pantalla que no tiene de dónde sacar
 * datos, no para experimentos.
 */

export const FLAG_ENV = {
  /** Campana de avisos del sidebar. Falta `GET /me/notifications`. */
  notifications: "VITE_FLAG_NOTIFICATIONS",
  /** Buscador ⌘K. La paleta todavía no existe. */
  commandSearch: "VITE_FLAG_COMMAND_SEARCH",
  /** Inicio: «Mientras no mirabas» contado por Clara. Falta `GET /home/today`. */
  homeOvernightSummary: "VITE_FLAG_HOME_OVERNIGHT_SUMMARY",
  /** Bandeja: vistas guardadas (Todas, Sin asignar, Mías…). Falta `crm.saved_views`. */
  inboxSavedViews: "VITE_FLAG_INBOX_SAVED_VIEWS",
  /** Bandeja: pestañas por estado (Nuevas, En curso…). Falta `conversation.status`. */
  inboxStatusTabs: "VITE_FLAG_INBOX_STATUS_TABS",
  /** Crear un agente hablando con Clara. Falta `POST /agents/builder-sessions`. */
  agentChatBuilder: "VITE_FLAG_AGENT_CHAT_BUILDER",
  /** Plantillas de Alquileres, Tasaciones y Postventa. El backend solo tiene Ventas. */
  agentExtraTemplates: "VITE_FLAG_AGENT_EXTRA_TEMPLATES",
  /** Derivación «por reglas» con grupos. Falta `collaborators.pools` y el modo `rules`. */
  agentRulesRouting: "VITE_FLAG_AGENT_RULES_ROUTING",
  /** El teléfono como canal de un agente. Falta `channels.kind = "voice"`. */
  agentVoiceChannel: "VITE_FLAG_AGENT_VOICE_CHANNEL",
  /** «Qué fichas» y «Zonas» del catálogo de un agente. Falta acotar la búsqueda por agente. */
  agentCatalogScope: "VITE_FLAG_AGENT_CATALOG_SCOPE",
  /** Probar una herramienta externa y ver sus ejecuciones. Falta el endpoint y el registro. */
  externalToolRunner: "VITE_FLAG_EXTERNAL_TOOL_RUNNER",
  /** Automatizaciones: «en los últimos 30 días hubiera agarrado a N». Falta `POST /automations/preview?window=30d`. */
  automationBacktest: "VITE_FLAG_AUTOMATION_BACKTEST",
  /** Integraciones: «Actualizar la llave» del CRM desde el cartel de error. Falta el endpoint. */
  crmKeyUpdate: "VITE_FLAG_CRM_KEY_UPDATE",
  /** Integraciones: la tarjeta de Meta Conversions API. Falta todo el circuito de eventos. */
  metaConversionsApi: "VITE_FLAG_META_CONVERSIONS_API",
  /** Contactos: el «Historial» del contacto. Falta `GET /contacts/{id}/timeline`. */
  contactTimeline: "VITE_FLAG_CONTACT_TIMELINE",
} as const;

export type FlagName = keyof typeof FLAG_ENV;
export type Flags = Readonly<Record<FlagName, boolean>>;

function isOn(value: unknown): boolean {
  return value === true || value === "true" || value === "1";
}

/** Lee los flags de un entorno. Separado para poder probarlo sin Vite. */
export function readFlags(env: Record<string, unknown>): Flags {
  const entries = (Object.keys(FLAG_ENV) as FlagName[]).map(
    (name) => [name, isOn(env[FLAG_ENV[name]])] as const,
  );
  return Object.freeze(Object.fromEntries(entries) as Record<FlagName, boolean>);
}

export const flags: Flags = readFlags(import.meta.env);

/** Se resuelven en build, así que no hace falta estado: es una lectura. */
export function useFlag(name: FlagName): boolean {
  return flags[name];
}
